<script setup lang="ts">
import { inject, ref, computed, useSlots, type HTMLAttributes } from "vue";
import { cn } from "@/lib/utils";
import { Skeleton } from "../skeleton";
import TableCell from "./TableCell.vue";
import { TABLE_COLUMN_COUNT_KEY, TABLE_IS_FETCHING_KEY } from "@/types/symbols";

const props = withDefaults(
  defineProps<{
    class?: HTMLAttributes["class"];
    isLoading?: boolean | null;
    skeletonHeight?: string;
  }>(),
  { class: undefined, isLoading: null, skeletonHeight: "h-4" },
);

const slots = useSlots();

// Injects the number of columns from the table component
const columns = inject(TABLE_COLUMN_COUNT_KEY, 1);

// Injects the is fetching flag from the query
const isFetching = inject(TABLE_IS_FETCHING_KEY, ref(false));

// Determines if loading skeleton should be shown
// - isLoading=true → show loading
// - isLoading=false → hide loading (overrides isFetching)
// - isLoading=null (not passed) → defer to isFetching
const showLoading = computed(() => {
  if (props.isLoading === true) return true;
  if (props.isLoading === false) return false;
  if (isFetching === undefined) return false;
  return isFetching.value;
});

// Check if slot has content - used to determine skeleton rendering strategy
const hasSlotContent = computed(() => !!slots.default?.().length);

// Use overlay approach only when loading AND slot has content (refetching)
// Otherwise use inline skeleton cells (initial load with empty slot)
const useOverlay = computed(() => showLoading.value && hasSlotContent.value);
</script>

<template>
  <tr
    data-slot="table-row"
    :class="
      cn(
        'hover:bg-muted data-[state=selected]:bg-muted border-border border-b transition-colors',
        // When refetching (overlay mode), add relative positioning and hide data cell contents
        // while keeping them in the DOM to preserve column widths
        useOverlay && 'relative [&>td:not(.absolute)]:opacity-0',
        props.class,
      )
    "
  >
    <!-- Initial load: no slot content, render skeleton cells inline -->
    <template v-if="showLoading && !hasSlotContent">
      <TableCell :colspan="columns">
        <Skeleton :class="cn('my-1 w-full', props.skeletonHeight)" />
      </TableCell>
    </template>

    <!-- Has data: render slot content -->
    <template v-else>
      <slot />
    </template>

    <!-- Refetching: overlay skeleton on top of existing content -->
    <TableCell v-if="useOverlay" :colspan="columns ?? 1" class="absolute inset-0 border-b-0! p-0!">
      <div class="flex h-full w-full items-center px-4">
        <Skeleton :class="cn('w-full', props.skeletonHeight)" />
      </div>
    </TableCell>
  </tr>
</template>
